import React, { useState, useEffect } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import emailService, { UnifiedThread, UnifiedMessage, UnifiedThreadsResponse, UnifiedThreadMessagesResponse } from '../../services/emailService';
import { ThreadList, ThreadView, MailboxSelector, EmptyState, LoadingState } from './inbox';
import toast from 'react-hot-toast';

// Define Message interface that matches what ThreadView expects
interface Message {
  id: string;
  message_id: string;
  thread_id: string;
  from: {
    email: string;
    name?: string;
  };
  to: {
    email: string;
    name?: string;
  }[];
  cc?: {
    email: string;
    name?: string;
  }[];
  bcc?: {
    email: string;
    name?: string;
  }[];
  subject: string; // Required, not optional
  text_body?: string;
  html_body?: string;
  snippet?: string;
  received_at: string;
  is_read: boolean;
  is_starred?: boolean;
  archived?: boolean;
  intent?: string;
  snooze_until?: string | null;
  assignee?: string | null;
  is_positive_response?: boolean;
  positive_response_reason?: string;
}

// Define Thread interface that matches what ThreadView expects
interface Thread {
  id: string;
  thread_id: string;
  subject: string;
  messages: Message[];
}

// Define mailbox interface
interface Mailbox {
  id: string;
  email: string;
  name: string;
  status: 'active' | 'paused' | 'error';
}

// Reply template interface
interface ReplyTemplate {
  id: string;
  name: string;
  subject?: string;
  body: string;
  category: 'interest' | 'followup' | 'demo' | 'pricing' | 'meeting' | 'other';
}

// Saved view interface
interface SavedView {
  id: string;
  name: string;
  icon: string;
  filter: (thread: UnifiedThread) => boolean;
}

interface UnifiedInboxProps {
  // Add any props here
}

const UnifiedInbox: React.FC<UnifiedInboxProps> = () => {
  // State
  const [filter, setFilter] = useState<'inbox' | 'starred' | 'archived'>('inbox');
  const [threads, setThreads] = useState<UnifiedThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [selectedMailbox, setSelectedMailbox] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [view, setView] = useState<'inbox' | 'starred' | 'archived'>('inbox');
  const [page, setPage] = useState(1);
  const [totalThreads, setTotalThreads] = useState(0);
  const [pageSize] = useState(20);
  const [isSyncing, setIsSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // New state for advanced features
  const [selectedThreads, setSelectedThreads] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ReplyTemplate | null>(null);
  const [activeView, setActiveView] = useState<string>('all');
  const [teamMembers] = useState(['Unassigned', 'Alice Johnson', 'Bob Smith', 'Charlie Davis', 'Diana Prince']);
  const [advancedFilters, setAdvancedFilters] = useState({
    campaign: '',
    dateRange: 'all',
    replyStatus: 'all',
    leadScore: 'all'
  });

  // Reply templates
  const replyTemplates: ReplyTemplate[] = [
    {
      id: 'interest',
      name: 'Initial Interest Response',
      category: 'interest',
      body: 'Thanks for your interest, {{firstName}}! I\'d love to learn more about your needs. When would be a good time for a quick 15-minute call this week?\n\nBest regards'
    },
    {
      id: 'followup',
      name: 'Follow-up',
      category: 'followup',
      body: 'Hi {{firstName}},\n\nJust following up on my previous message. I wanted to make sure this didn\'t get buried in your inbox.\n\nWould you have 10 minutes this week to discuss how we can help {{company}}?\n\nBest regards'
    },
    {
      id: 'demo',
      name: 'Demo Booking',
      category: 'demo',
      body: 'Great to hear you\'re interested, {{firstName}}!\n\nI\'d love to show you a personalized demo. Here\'s my calendar link: [INSERT CALENDAR LINK]\n\nLooking forward to connecting!'
    },
    {
      id: 'pricing',
      name: 'Pricing Information',
      category: 'pricing',
      body: 'Hi {{firstName}},\n\nThanks for asking about pricing! Our plans start at $X/month and scale based on your needs.\n\nI\'d be happy to walk you through the options and find the best fit for {{company}}. Would you like to schedule a quick call?\n\nBest regards'
    },
    {
      id: 'meeting',
      name: 'Meeting Confirmation',
      category: 'meeting',
      body: 'Perfect! I\'ve added our meeting to my calendar for [DATE/TIME].\n\nWe\'ll discuss:\n- Your current challenges\n- How we can help\n- Next steps\n\nLooking forward to it, {{firstName}}!'
    },
    {
      id: 'ooo',
      name: 'Out of Office Response',
      category: 'other',
      body: 'Thanks for letting me know, {{firstName}}. I\'ll follow up with you when you\'re back.\n\nEnjoy your time off!'
    }
  ];

  // Saved views for quick filtering
  const savedViews: SavedView[] = [
    {
      id: 'all',
      name: 'All Messages',
      icon: '📬',
      filter: () => true
    },
    {
      id: 'hot',
      name: 'Hot Leads',
      icon: '🔥',
      filter: (t) => ((t as any).lead_score || 0) >= 80
    },
    {
      id: 'warm',
      name: 'Warm Leads',
      icon: '☀️',
      filter: (t) => ((t as any).lead_score || 0) >= 50 && ((t as any).lead_score || 0) < 80
    },
    {
      id: 'positive',
      name: 'Positive Responses',
      icon: '✅',
      filter: (t) => (t as any).has_positive_response
    },
    {
      id: 'needs_reply',
      name: 'Needs Reply',
      icon: '⏰',
      filter: (t) => !(t as any).has_reply && t.message_count > 0
    },
    {
      id: 'assigned_to_me',
      name: 'My Assigned',
      icon: '👤',
      filter: (t) => (t as any).assignee === 'current_user' // TODO: Replace with actual user
    }
  ];

  // Load mailboxes on mount
  useEffect(() => {
    const loadMailboxes = async () => {
      try {
        const emailMailboxes = await emailService.getMailboxes();
        
        // Convert EmailMailbox[] to Mailbox[] by ensuring all required fields
        const convertedMailboxes: Mailbox[] = emailMailboxes
          .filter(mailbox => mailbox.id !== undefined) // Filter out any without IDs
          .map(mailbox => ({
            id: mailbox.id as string, // We've filtered out undefined IDs
            email: mailbox.email,
            name: mailbox.name,
            status: mailbox.status
          }));
        
        setMailboxes(convertedMailboxes);
        
        // Set the first mailbox as selected if none is selected yet
        if (convertedMailboxes.length > 0 && !selectedMailbox) {
          setSelectedMailbox(convertedMailboxes[0].id || null);
        }
      } catch (error) {
        console.error('Error loading mailboxes:', error);
        toast.error('Failed to load mailboxes');
      }
    };
    
    loadMailboxes();
  }, []);

  // Load threads based on selected mailbox and view
  useEffect(() => {
    if (selectedMailbox) {
      loadThreads();
    }
  }, [selectedMailbox, view]);

  // Set up polling for new messages every 5 minutes, but only if mailboxes are connected
  useEffect(() => {
    // Only set up polling if there are connected mailboxes
    if (mailboxes.length === 0) {
      return;
    }

    // Initial log
    console.log('Setting up 5-minute polling for new messages');
    
    // Set up polling interval (5 minutes = 300000 ms)
    const pollingInterval = setInterval(() => {
      // Only poll if there are mailboxes and one is selected
      if (mailboxes.length > 0 && selectedMailbox) {
        console.log('Polling for new messages...');
        loadThreads();
      }
    }, 5 * 60 * 1000); // 5 minutes in milliseconds
    
    // Clean up interval when component unmounts
    return () => {
      console.log('Clearing message polling interval');
      clearInterval(pollingInterval);
    };
  }, [mailboxes.length, selectedMailbox]); // Re-run effect if mailboxes change

  // Load threads
  const loadThreads = async () => {
    setIsLoading(true);
    try {
      const response = await emailService.getUnifiedThreads(view);
      setThreads(response.threads || []);
      setTotalThreads(response.total || 0);
    } catch (error) {
      console.error('Failed to load threads:', error);
      toast.error('Failed to load threads');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to get thread messages
  const loadThreadMessages = async (threadId: string) => {
    setIsLoadingMessages(true);
    try {
      const response = await emailService.getUnifiedThreadMessages(threadId);
      
      // Convert UnifiedMessage[] to Message[] expected by ThreadView
      const convertedMessages = response.messages.map(msg => ({
        id: msg.id,
        message_id: msg.message_id,
        thread_id: msg.thread_id,
        from: {
          email: msg.from_address,
          name: msg.from_name
        },
        to: msg.to_addresses.map(email => ({ email })),
        subject: msg.subject || '', // Ensure subject is never undefined
        text_body: msg.body_text,
        html_body: msg.body_html,
        intent: msg.intent,
        snooze_until: msg.snooze_until || null,
        assignee: msg.assignee || null,
        received_at: msg.received_at,
        is_read: msg.is_read,
        is_starred: msg.is_starred || false,
        archived: msg.is_archived || false
      }));
      
      setMessages(convertedMessages);
    } catch (error) {
      console.error('Error loading thread messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Load messages when selected thread changes
  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedThread) {
        setMessages([]);
        return;
      }
      
      try {
        const response = await emailService.getUnifiedThreadMessages(selectedThread);
        
        // Convert UnifiedMessage[] to Message[] expected by ThreadView
        const convertedMessages = response.messages.map(msg => ({
          id: msg.id,
          message_id: msg.message_id,
          thread_id: msg.thread_id,
          from: {
            email: msg.from_address,
            name: msg.from_name
          },
          to: msg.to_addresses.map(email => ({ email })),
          subject: msg.subject || '', // Ensure subject is never undefined
          text_body: msg.body_text,
          html_body: msg.body_html,
          intent: msg.intent,
          snooze_until: msg.snooze_until || null,
          assignee: msg.assignee || null,
          received_at: msg.received_at,
          is_read: msg.is_read,
          is_starred: msg.is_starred || false,
          archived: msg.is_archived || false
        }));
        
        setMessages(convertedMessages);
      } catch (error) {
        console.error('Failed to load messages:', error);
        toast.error('Failed to load messages');
        setMessages([]);
      }
    };
    
    loadMessages();
  }, [selectedThread, toast]);

  // Handle sync button click
  const handleSync = async (mailboxId: string) => {
    if (!mailboxId) return;
    
    setIsSyncing(true);
    try {
      const result = await emailService.syncUnifiedMailbox(mailboxId);
      if (result.success) {
        toast.success(`Synced ${result.messageCount} messages`);
        // Refresh threads after sync
        await loadThreads();
      } else {
        toast.error('Failed to sync mailbox');
      }
    } catch (error) {
      console.error('Failed to sync mailbox:', error);
      toast.error('Failed to sync mailbox');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleThreadSelect = (threadId: string) => {
    setSelectedThread(threadId);
  };

  // Handle filter changes
  const handleFilterChange = (newFilter: 'inbox' | 'starred' | 'archived') => {
    setView(newFilter);
    setPage(1); // Reset to first page
    setSelectedThread(null); // Clear selected thread
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement search functionality
    console.log('Searching for:', searchQuery);
  };

  // Handle pagination
  const handleNextPage = () => {
    if (page * pageSize < totalThreads) {
      setPage(page + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  // Handle message actions (read, star, archive, etc)
  const handleMessageAction = async (
    action: 'read' | 'unread' | 'star' | 'unstar' | 'archive' | 'unarchive' | 'positive_response' | 'remove_positive_response', 
    id: string,
    additionalData?: string
  ) => {
    try {
      let result;
      
      // Handle different message actions
      switch (action) {
        case 'read':
          result = await emailService.markUnifiedMessageRead(id, true);
          break;
        case 'unread':
          result = await emailService.markUnifiedMessageRead(id, false);
          break;
        case 'star':
          result = await emailService.starUnifiedMessage(id, true);
          break;
        case 'unstar':
          result = await emailService.starUnifiedMessage(id, false);
          break;
        case 'archive':
          result = await emailService.archiveUnifiedMessage(id, true);
          break;
        case 'unarchive':
          result = await emailService.archiveUnifiedMessage(id, false);
          break;
        case 'positive_response':
          result = await emailService.markAsPositiveResponse(id, true, additionalData);
          break;
        case 'remove_positive_response':
          result = await emailService.markAsPositiveResponse(id, false);
          break;
        default:
          console.warn(`Unknown message action: ${action}`);
          return;
      }
      
      // If successful, reload threads and messages
      if (result && result.success) {
        if (selectedThread) {
          // Reload messages for current thread
          const response = await emailService.getUnifiedThreadMessages(selectedThread);
          
          // Convert UnifiedMessage[] to Message[]
          const convertedMessages = response.messages.map(msg => ({
            id: msg.id,
            message_id: msg.message_id,
            thread_id: msg.thread_id,
            from: {
              email: msg.from_address,
              name: msg.from_name
            },
            to: msg.to_addresses.map(email => ({ email })),
            subject: msg.subject || '', 
            text_body: msg.body_text,
            html_body: msg.body_html,
            intent: msg.intent,
            snooze_until: msg.snooze_until || null,
            assignee: msg.assignee || null,
            received_at: msg.received_at,
            is_read: msg.is_read,
            is_starred: msg.is_starred || false,
            archived: msg.is_archived || false
          }));
          
          setMessages(convertedMessages);
        }
        
        // Reload threads list
        await emailService.getUnifiedThreads(view).then(response => {
          setThreads(response.threads || []);
        });
      }
    } catch (error) {
      console.error(`Failed to perform action ${action}:`, error);
      toast.error(`Failed to perform action ${action}`);
    }
  };

  // Handle sending a reply
  const handleSendReply = async (messageId: string, content: string, isHtml: boolean = true) => {
    try {
      const result = await emailService.sendUnifiedReply(messageId, content, isHtml);
      if (result && result.success) {
        toast.success('Reply sent successfully');
        
        // Reload messages to show the new reply
        if (selectedThread) {
          const response = await emailService.getUnifiedThreadMessages(selectedThread);
          
          // Convert UnifiedMessage[] to Message[]
          const convertedMessages = response.messages.map(msg => ({
            id: msg.id,
            message_id: msg.message_id,
            thread_id: msg.thread_id,
            from: {
              email: msg.from_address,
              name: msg.from_name
            },
            to: msg.to_addresses.map(email => ({ email })),
            subject: msg.subject || '', 
            text_body: msg.body_text,
            html_body: msg.body_html,
            received_at: msg.received_at,
            is_read: msg.is_read,
            is_starred: msg.is_starred || false,
            archived: msg.is_archived || false
          }));
          
          setMessages(convertedMessages);
        }
      } else {
        toast.error('Failed to send reply');
      }
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error('Failed to send reply');
    }
  };

  // Handle snooze
  const handleSnoozeMessage = async (messageId: string, untilIso: string) => {
    try {
      const res = await emailService.snoozeUnifiedMessage(messageId, untilIso);
      if (res?.success) {
        toast.success('Message snoozed');
        if (selectedThread) {
          await loadThreadMessages(selectedThread);
        }
        await loadThreads();
      }
    } catch (e) {
      console.error('Failed to snooze message', e);
      toast.error('Failed to snooze message');
    }
  };

  // Handle assign
  const handleAssignMessage = async (messageId: string, assignee: string) => {
    try {
      const res = await emailService.assignUnifiedMessage(messageId, assignee);
      if (res?.success) {
        toast.success('Message assigned');
        if (selectedThread) {
          await loadThreadMessages(selectedThread);
        }
        await loadThreads();
      }
    } catch (e) {
      console.error('Failed to assign message', e);
      toast.error('Failed to assign message');
    }
  };

  // NEW: Handle bulk actions
  const handleBulkAction = async (action: 'archive' | 'star' | 'assign', value?: string) => {
    if (selectedThreads.size === 0) {
      toast.error('No threads selected');
      return;
    }

    try {
      const threadIds = Array.from(selectedThreads);
      let successCount = 0;

      for (const threadId of threadIds) {
        try {
          switch (action) {
            case 'archive':
              await handleMessageAction('archive', threadId);
              break;
            case 'star':
              await handleMessageAction('star', threadId);
              break;
            case 'assign':
              if (value) await handleAssignMessage(threadId, value);
              break;
          }
          successCount++;
        } catch (error) {
          console.error(`Failed to ${action} thread ${threadId}:`, error);
        }
      }

      toast.success(`${action === 'archive' ? 'Archived' : action === 'star' ? 'Starred' : 'Assigned'} ${successCount} thread(s)`);
      setSelectedThreads(new Set());
      setShowBulkActions(false);
      await loadThreads();
    } catch (error) {
      console.error(`Bulk ${action} failed:`, error);
      toast.error(`Failed to ${action} selected threads`);
    }
  };

  // NEW: Handle thread selection
  const handleToggleThreadSelection = (threadId: string) => {
    const newSelection = new Set(selectedThreads);
    if (newSelection.has(threadId)) {
      newSelection.delete(threadId);
    } else {
      newSelection.add(threadId);
    }
    setSelectedThreads(newSelection);
    setShowBulkActions(newSelection.size > 0);
  };

  // NEW: Handle select all threads
  const handleSelectAllThreads = () => {
    if (selectedThreads.size === threads.length) {
      setSelectedThreads(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedThreads(new Set(threads.map(t => t.id)));
      setShowBulkActions(true);
    }
  };

  // NEW: Handle template insertion
  const handleInsertTemplate = (template: ReplyTemplate) => {
    setSelectedTemplate(template);
    setShowTemplates(false);
    toast.success(`Template "${template.name}" inserted`);
    // TODO: Actually insert the template content into the reply composer
  };

  // NEW: Apply saved view filter
  const getFilteredThreads = () => {
    const activeViewConfig = savedViews.find(v => v.id === activeView);
    if (!activeViewConfig) return threads;
    return threads.filter(activeViewConfig.filter);
  };

  
  // Render empty state if no mailboxes
  if (mailboxes.length === 0 && !isLoading) {
    return (
      <EmptyState
        icon={({ className }) => (
          <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )}
        title="No Mailboxes Found"
        description="Please add a mailbox to use the unified inbox"
        actionText="Add Mailbox"
        actionHref="/email/settings"
      />
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-5">
      <h1 className="text-2xl font-bold mb-4">Unified Inbox</h1>
      
      <div className="flex items-center mb-4">
        <MailboxSelector
          mailboxes={mailboxes}
          selectedMailbox={selectedMailbox}
          onSelectMailbox={(mailboxId: string) => setSelectedMailbox(mailboxId)}
        />
        
        <button
          className={`ml-2 flex items-center px-4 py-2 border border-gray-300 rounded-md ${isSyncing ? 'bg-gray-100' : 'bg-white hover:bg-gray-50'}`}
          disabled={isSyncing || !selectedMailbox}
          onClick={() => selectedMailbox && handleSync(selectedMailbox)}
        >
          {isSyncing ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Syncing</span>
            </>
          ) : (
            <>
              <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Sync</span>
            </>
          )}
        </button>
        
        {/* Templates Dropdown */}
        <div className="ml-2 relative">
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Templates ({replyTemplates.length})</span>
          </button>
          
          {showTemplates && (
            <div className="absolute top-full mt-2 right-0 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-[500px] overflow-hidden flex flex-col">
              <div className="p-3 border-b border-gray-100 bg-gray-50">
                <h4 className="text-sm font-semibold text-gray-900">Quick Reply Templates</h4>
                <p className="text-xs text-gray-500 mt-1">Click to view template content</p>
              </div>
              <div className="overflow-y-auto flex-1">
                {replyTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleInsertTemplate(template)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{template.name}</div>
                        <div className="text-xs text-gray-500 mt-1 line-clamp-2">{template.body.substring(0, 100)}...</div>
                      </div>
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                        {template.category}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="p-3 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={() => setShowTemplates(false)}
                  className="text-xs text-gray-600 hover:text-gray-900"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex items-center mb-4">
        <button
          className={`flex items-center px-4 py-2 mr-2 rounded-md ${filter === 'inbox' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
          onClick={() => handleFilterChange('inbox')}
        >
          <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <span>Inbox</span>
        </button>
        <button
          className={`flex items-center px-4 py-2 mr-2 rounded-md ${filter === 'starred' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
          onClick={() => handleFilterChange('starred')}
        >
          <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
          <span>Starred</span>
        </button>
        <button
          className={`flex items-center px-4 py-2 mr-2 rounded-md ${filter === 'archived' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
          onClick={() => handleFilterChange('archived')}
        >
          <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
          <span>Archived</span>
        </button>
        
        <div className="flex-1">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input 
                type="text"
                className="pl-10 w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search emails..." 
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              />
            </div>
          </form>
        </div>
      </div>
      
      {isLoading ? (
        <LoadingState />
      ) : (
        <div className="flex border border-gray-200 rounded-md overflow-hidden">
          {/* Saved Views Sidebar */}
          <div className="w-[200px] border-r border-gray-200 bg-gray-50 h-[70vh] overflow-auto">
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Views</h3>
              <nav className="space-y-1">
                {savedViews.map((view) => (
                  <button
                    key={view.id}
                    onClick={() => setActiveView(view.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                      activeView === view.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="mr-2">{view.icon}</span>
                    <span className="truncate">{view.name}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="flex-1 flex flex-col">
            {/* Bulk Actions Bar */}
            {showBulkActions && (
              <div className="bg-blue-50 border-b border-blue-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedThreads.size} selected
                  </span>
                  <button
                    onClick={() => handleBulkAction('archive')}
                    className="px-3 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50"
                  >
                    Archive
                  </button>
                  <button
                    onClick={() => handleBulkAction('star')}
                    className="px-3 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50"
                  >
                    Star
                  </button>
                  <select
                    onChange={(e) => {
                      if (e.target.value) handleBulkAction('assign', e.target.value);
                    }}
                    className="px-3 py-1 bg-white border border-gray-300 rounded text-sm"
                    defaultValue=""
                  >
                    <option value="">Assign to...</option>
                    {teamMembers.map((member) => (
                      <option key={member} value={member}>{member}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => {
                    setSelectedThreads(new Set());
                    setShowBulkActions(false);
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Clear selection
                </button>
              </div>
            )}

            <div className="flex flex-1 overflow-hidden">
              <div 
                className="w-[350px] border-r border-gray-200 overflow-auto bg-white"
              >
                {/* Select All Checkbox */}
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center sticky top-0 z-10">
                  <input
                    type="checkbox"
                    checked={selectedThreads.size === getFilteredThreads().length && getFilteredThreads().length > 0}
                    onChange={handleSelectAllThreads}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-600">Select all ({getFilteredThreads().length})</span>
                </div>

                <ThreadList 
                  threads={getFilteredThreads()}
                  onSelectThread={setSelectedThread}
                  selectedThreadId={selectedThread}
                  selectedThreads={selectedThreads}
                  onToggleThreadSelection={handleToggleThreadSelection}
                />
            
            {totalThreads > 0 && (
              <div className="flex justify-between p-2 border-t border-gray-200">
                <button 
                  className={`px-2 py-1 text-sm rounded-md ${page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
                  onClick={handlePrevPage}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span className="text-sm flex items-center">
                  Page {page} of {Math.ceil(totalThreads / pageSize)}
                </span>
                <button 
                  className={`px-2 py-1 text-sm rounded-md ${page * pageSize >= totalThreads ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
                  onClick={handleNextPage}
                  disabled={page * pageSize >= totalThreads}
                >
                  Next
                </button>
              </div>
            )}
              </div>
              
              <div className="flex-1 overflow-auto bg-white">
                {selectedThread ? (
                  <ThreadView 
                    thread={{
                      id: selectedThread || '',
                      thread_id: selectedThread || '',
                      subject: messages.length > 0 ? messages[0].subject : '',
                      messages: messages
                    }}
                    onMarkAsRead={(messageId) => handleMessageAction('read', messageId)}
                    onToggleStar={(messageId, starred) => handleMessageAction(starred ? 'star' : 'unstar', messageId)}
                    onArchive={(threadId) => handleMessageAction('archive', threadId)}
                    onMoveToInbox={(threadId) => handleMessageAction('unarchive', threadId)}
                    onSendReply={(threadId, messageId, content) => handleSendReply(messageId, content)}
                    onMarkPositiveResponse={(messageId, isPositive, reason) => 
                      handleMessageAction(
                        isPositive ? 'positive_response' : 'remove_positive_response', 
                        messageId, 
                        reason
                      )
                    }
                    onSnoozeMessage={handleSnoozeMessage}
                    onAssignMessage={handleAssignMessage}
                  />
                ) : (
                  <EmptyState
                    icon={({ className }) => (
                      <svg
                        className={className}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    )}
                    title="No messages yet"
                    description="Connect a mailbox to start receiving messages in your unified inbox."
                    actionText="Add a mailbox"
                    actionHref="/email/mailboxes/new"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedInbox;
